import { AxiosError } from 'axios';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import { MaterialsLibrary } from './MaterialsLibrary';
import { TemplateEditor } from './TemplateEditor';

type ProfileTab = 'prices' | 'materials' | 'pipe-templates' | 'binding-templates';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type WorkPrice = {
  id: string;
  code: string;
  title: string;
  unit: string;
  price: number;
};

type PriceRow = WorkPrice & {
  draft: string;
  rowError: string | null;
};

type SaveResult = 'saved' | 'validation' | 'network' | 'ignored';

function readProfileTab(value: string | null): ProfileTab {
  if (value === 'materials' || value === 'pipe-templates' || value === 'binding-templates') {
    return value;
  }

  return 'prices';
}

const TABS: { id: ProfileTab; label: string }[] = [
  { id: 'prices', label: 'Прайс работ' },
  { id: 'materials', label: 'Библиотека материалов' },
  { id: 'pipe-templates', label: 'Шаблоны труб' },
  { id: 'binding-templates', label: 'Шаблоны обвязки' },
];

const UNIT_LABELS: Record<string, string> = {
  m: 'м',
  pcs: 'шт',
};

const SAVE_ERROR_TEXT = 'Не удалось сохранить. Проверьте соединение и повторите.';

function unitLabel(unit: string): string {
  return UNIT_LABELS[unit] ?? unit;
}

function formatPrice(price: number): string {
  if (!Number.isFinite(price)) {
    return '0';
  }

  return String(price);
}

const PRICE_DIGIT_LIMIT = 12;
const PRICE_FRACTION_LIMIT = 2;
const PRICE_PARSE_ERROR = 'Введите число от 0: не больше 12 цифр и двух знаков после запятой.';

/** Пустая строка — 0. «.5» и «,5» — 0.5. Лишние знаки после запятой отрезаются. */
function readPrice(draft: string): { price: number; text: string } | null {
  let trimmed = draft.trim().replace(',', '.').replace(/\.$/, '');
  if (trimmed.startsWith('.')) {
    trimmed = `0${trimmed}`;
  }

  if (trimmed === '') {
    return { price: 0, text: '0' };
  }

  const match = /^(\d+)(?:\.(\d+))?$/.exec(trimmed);
  if (!match) {
    return null;
  }

  const intPart = match[1].replace(/^0+(?=\d)/, '');
  const fracPart = (match[2] ?? '').slice(0, PRICE_FRACTION_LIMIT);
  const significant = `${intPart}${fracPart}`.replace(/^0+/, '');
  if (significant.length > PRICE_DIGIT_LIMIT) {
    return null;
  }

  const text = fracPart.length > 0 ? `${intPart}.${fracPart}` : intPart;
  const price = Number(text);
  if (!Number.isFinite(price) || price < 0) {
    return null;
  }

  return { price, text };
}

function parsePrice(draft: string): number | null {
  return readPrice(draft)?.price ?? null;
}

function samePrice(left: number, right: number): boolean {
  return Math.abs(left - right) < 0.000001;
}

function draftMatchesPrice(draft: string, price: number): boolean {
  const parsed = parsePrice(draft);
  return parsed !== null && samePrice(parsed, price);
}

function sanitizePriceInput(raw: string): string {
  const withoutMinus = raw.replace(/[\u2212\u2013\u2014-]/g, '');
  let separatorSeen = false;
  let intDigits = 0;
  let fracDigits = 0;
  let result = '';

  for (const char of withoutMinus) {
    if (char >= '0' && char <= '9') {
      if (separatorSeen) {
        if (fracDigits >= PRICE_FRACTION_LIMIT || intDigits + fracDigits >= PRICE_DIGIT_LIMIT) {
          continue;
        }

        fracDigits += 1;
        result += char;
        continue;
      }

      if (intDigits >= PRICE_DIGIT_LIMIT) {
        continue;
      }

      intDigits += 1;
      result += char;
      continue;
    }

    if ((char === '.' || char === ',') && !separatorSeen) {
      separatorSeen = true;
      result += char;
    }
  }

  return result;
}

function isTransportError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) {
    return true;
  }

  if (!error.response) {
    return true;
  }

  return error.response.status >= 500;
}

function validationText(error: AxiosError): string {
  const data = error.response?.data as { detail?: unknown } | undefined;
  const detail = data?.detail;

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item && typeof item.msg === 'string') {
          return item.msg.trim();
        }

        return '';
      })
      .filter((message) => message.length > 0);

    if (messages.length > 0) {
      return messages.join(' ');
    }
  }

  return 'Сервер отклонил цену.';
}

function saveStatusText(status: SaveStatus): string {
  if (status === 'saving') {
    return 'Сохранение…';
  }

  if (status === 'saved') {
    return 'Сохранено';
  }

  if (status === 'error') {
    return SAVE_ERROR_TEXT;
  }

  return '';
}

export function ProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = readProfileTab(searchParams.get('tab'));
  const [rows, setRows] = useState<PriceRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const rowsRef = useRef<PriceRow[]>([]);
  const savingIdsRef = useRef<Set<string>>(new Set());
  const resaveIdsRef = useRef<Set<string>>(new Set());
  const inflightRef = useRef(0);
  const hadNetworkErrorRef = useRef(false);
  const hadSavedRef = useRef(false);
  const mountedRef = useRef(true);

  rowsRef.current = rows;

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadPrices() {
      setIsLoading(true);
      setLoadError(null);

      let unauthorized = false;

      try {
        const response = await apiClient.get<{ items?: WorkPrice[] }>('/profile/work-prices');
        if (cancelled) {
          return;
        }

        const items = Array.isArray(response.data.items) ? response.data.items : [];
        setRows(
          items.map((item) => ({
            ...item,
            draft: formatPrice(item.price),
            rowError: null,
          })),
        );
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (error instanceof AxiosError && error.response?.status === 401) {
          unauthorized = true;
          return;
        }

        setLoadError('Не удалось загрузить прайс. Проверьте соединение и повторите.');
      } finally {
        if (!cancelled && !unauthorized) {
          setIsLoading(false);
        }
      }
    }

    void loadPrices();

    return () => {
      cancelled = true;
    };
  }, []);

  function updateRow(rowId: string, patch: Partial<PriceRow>) {
    const nextRows = rowsRef.current.map((item) => (item.id === rowId ? { ...item, ...patch } : item));
    rowsRef.current = nextRows;

    if (mountedRef.current) {
      setRows(nextRows);
    }
  }

  function noteSaveStart() {
    inflightRef.current += 1;

    if (mountedRef.current) {
      setSaveStatus('saving');
    }
  }

  function noteSaveEnd(result: SaveResult) {
    if (result === 'network') {
      hadNetworkErrorRef.current = true;
    }

    if (result === 'saved') {
      hadSavedRef.current = true;
    }

    inflightRef.current = Math.max(0, inflightRef.current - 1);

    if (inflightRef.current > 0 || !mountedRef.current) {
      return;
    }

    if (hadNetworkErrorRef.current) {
      setSaveStatus('error');
    } else if (hadSavedRef.current) {
      setSaveStatus('saved');
    } else {
      setSaveStatus('idle');
    }

    hadNetworkErrorRef.current = false;
    hadSavedRef.current = false;
  }

  async function saveRow(rowId: string) {
    if (savingIdsRef.current.has(rowId)) {
      resaveIdsRef.current.add(rowId);
      return;
    }

    const row = rowsRef.current.find((item) => item.id === rowId);
    if (!row) {
      return;
    }

    const parsed = readPrice(row.draft);
    if (!parsed || parsed.price < 0) {
      updateRow(rowId, { rowError: PRICE_PARSE_ERROR });
      return;
    }

    const nextPrice = parsed.price;

    if (samePrice(nextPrice, row.price)) {
      updateRow(rowId, { draft: formatPrice(nextPrice), rowError: null });
      return;
    }

    updateRow(rowId, { draft: formatPrice(nextPrice), rowError: null });

    savingIdsRef.current.add(rowId);
    noteSaveStart();
    let result: SaveResult = 'ignored';

    try {
      const response = await apiClient.patch<WorkPrice>(`/profile/work-prices/${rowId}`, {
        price: nextPrice,
      });
      result = 'saved';
      const currentDraft = rowsRef.current.find((item) => item.id === rowId)?.draft ?? row.draft;
      updateRow(rowId, {
        id: response.data.id,
        code: response.data.code,
        title: response.data.title,
        unit: response.data.unit,
        price: response.data.price,
        draft: draftMatchesPrice(currentDraft, nextPrice)
          ? formatPrice(response.data.price)
          : currentDraft,
        rowError: null,
      });
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 401) {
        result = 'ignored';
      } else if (error instanceof AxiosError && error.response?.status === 422) {
        result = 'validation';
        updateRow(rowId, { rowError: validationText(error) });
      } else if (isTransportError(error)) {
        result = 'network';
        if (!resaveIdsRef.current.has(rowId)) {
          updateRow(rowId, { rowError: SAVE_ERROR_TEXT });
        }
      } else {
        result = 'validation';
        const message = error instanceof AxiosError ? validationText(error) : 'Сервер отклонил цену.';
        updateRow(rowId, { rowError: message });
      }
    } finally {
      savingIdsRef.current.delete(rowId);
      const shouldResave = resaveIdsRef.current.delete(rowId);
      noteSaveEnd(result);

      if (shouldResave) {
        void saveRow(rowId);
      }
    }
  }

  function selectTab(tab: ProfileTab) {
    const next = new URLSearchParams(searchParams);
    if (tab === 'prices') {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }

    const kind = next.get('kind');
    const allowed =
      tab === 'pipe-templates'
        ? ['pipe_pex', 'pipe_pp']
        : tab === 'binding-templates'
          ? ['binding_pex', 'binding_pp']
          : [];
    if (!kind || !allowed.includes(kind)) {
      next.delete('kind');
    }

    setSearchParams(next, { replace: true });
  }

  function handleDraftChange(rowId: string, raw: string) {
    updateRow(rowId, { draft: sanitizePriceInput(raw), rowError: null });
  }

  function handlePriceKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === '-' || event.key === 'Subtract') {
      event.preventDefault();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
  }

  return (
    <main style={{ display: 'grid', gap: '16px' }}>
      <h1>Личный кабинет</h1>

      <nav style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }} aria-label="Разделы кабинета">
        {TABS.map((tab) => {
          const isActive = tab.id === activeTab;

          return (
            <button
              key={tab.id}
              type="button"
              className={isActive ? 'btn btn-active' : 'btn'}
              onClick={() => selectTab(tab.id)}
              aria-pressed={isActive}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>

      {activeTab === 'prices' ? (
        <section style={{ display: 'grid', gap: '12px' }}>
          <p
            aria-live="polite"
            style={{
              minHeight: '1.25em',
              margin: 0,
              color: saveStatus === 'error' ? '#b91c1c' : '#6b7280',
            }}
          >
            {saveStatusText(saveStatus)}
          </p>

          {isLoading ? <p>Загружаем прайс...</p> : null}

          {!isLoading && loadError ? (
            <p role="alert" style={{ color: '#b91c1c', margin: 0 }}>
              {loadError}
            </p>
          ) : null}

          {!isLoading && !loadError ? (
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                background: '#ffffff',
                overflow: 'hidden',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Название</th>
                    <th style={headerCellStyle}>Единица</th>
                    <th style={headerCellStyle}>Цена</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <PriceTableRow
                      key={row.id}
                      row={row}
                      onDraftChange={handleDraftChange}
                      onKeyDown={handlePriceKeyDown}
                      onBlur={(rowId) => {
                        void saveRow(rowId);
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : activeTab === 'materials' ? (
        <MaterialsLibrary />
      ) : activeTab === 'pipe-templates' ? (
        <TemplateEditor group="pipe" />
      ) : (
        <TemplateEditor group="binding" />
      )}
    </main>
  );
}

const headerCellStyle = {
  textAlign: 'left' as const,
  padding: '10px 12px',
  borderBottom: '1px solid #e5e7eb',
  color: '#374151',
  fontWeight: 600,
};

const bodyCellStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid #e5e7eb',
  verticalAlign: 'middle' as const,
};

function PriceTableRow({
  row,
  onDraftChange,
  onKeyDown,
  onBlur,
}: {
  row: PriceRow;
  onDraftChange: (rowId: string, raw: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onBlur: (rowId: string) => void;
}) {
  const errorId = `price-error-${row.id}`;

  return (
    <>
      <tr>
        <td style={bodyCellStyle}>{row.title}</td>
        <td style={bodyCellStyle}>{unitLabel(row.unit)}</td>
        <td style={bodyCellStyle}>
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            aria-label={`Цена: ${row.title}`}
            aria-invalid={row.rowError ? true : undefined}
            aria-describedby={row.rowError ? errorId : undefined}
            value={row.draft}
            onChange={(event) => onDraftChange(row.id, event.target.value)}
            onKeyDown={onKeyDown}
            onBlur={() => onBlur(row.id)}
            style={{ width: '140px', padding: '8px' }}
          />
        </td>
      </tr>
      {row.rowError ? (
        <tr>
          <td colSpan={3} style={{ ...bodyCellStyle, paddingTop: 0 }}>
            <p id={errorId} role="alert" style={{ color: '#b91c1c', margin: 0 }}>
              {row.rowError}
            </p>
          </td>
        </tr>
      ) : null}
    </>
  );
}
